// src/lib/advisorQuoteExport.js
// §7bis — Couche d'export STABLE et vendor-neutral. ARTKIV prépare les données de
// facturation dans une structure figée qui peut alimenter Pennylane, un autre
// logiciel de facturation ou une future plateforme agréée, SANS coupler
// l'application à un fournisseur. ARTKIV n'émet pas la facture réglementaire :
// l'export sert d'import vers l'outil de l'Advisor.
//
// Schéma (versionné) conforme au doc §7bis :
//   advisor_billing_identity, client_billing_identity, quote, mission,
//   invoice_lines[], tax_context, attachments[]

export const QUOTE_EXPORT_SCHEMA_VERSION = "1.0";

const num = (v) => (Number(v) || 0);
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

function lineTotalExTax(l) {
  return round2(num(l.qty) * num(l.unit_price_ex_tax) * (1 - num(l.discount_pct) / 100));
}

/**
 * Assemble la structure d'export stable à partir des données du devis.
 * @param {Object} p { quote, lines, advisorProfile, mission, client, totals }
 *   - quote : { internal_number, status, object, currency, valid_until,
 *               delivery_terms, payment_terms, notes, exclusions,
 *               discount_amount, deposit_amount, subtotal_ex_tax, tax_amount, total_inc_tax }
 *   - client : snapshot identité facturation client ({type,zone,name,email,address,siren,vat,po_ref})
 *   - advisorProfile : ligne advisor_billing_profiles
 *   - mission : ligne advisor_missions
 *   - lines : lignes de prestations/débours
 */
export function buildQuoteExport({ quote = {}, lines = [], advisorProfile = {}, mission = {}, client = {}, totals = {} }) {
  const a = advisorProfile || {};
  return {
    schema_version: QUOTE_EXPORT_SCHEMA_VERSION,
    generator: "artkiv",
    document_type: "quote",
    generated_at: new Date().toISOString(),
    // ⚠️ Ce n'est pas une facture réglementaire : numéro interne ≠ numéro comptable.
    regulatory_invoice: false,

    advisor_billing_identity: {
      legal_name: a.legal_name || null,
      trade_name: a.trade_name || null,
      legal_form: a.legal_form || null,
      siren: a.siren || null,
      siret: a.siret || null,
      vat_number: a.vat_number || null,
      vat_regime: a.vat_regime || null,
      default_vat_rate: a.default_vat_rate != null ? Number(a.default_vat_rate) : null,
      vat_exemption_mention: a.vat_exemption_mention || null,
      billing_email: a.billing_email || null,
      address: {
        line1: a.address_line1 || null,
        postal_code: a.postal_code || null,
        city: a.city || null,
        country_code: a.country_code || null,
      },
    },

    client_billing_identity: {
      type: client.type || null,          // individual | company
      zone: client.zone || null,          // fr | eu | non_eu
      name: client.name || null,
      email: client.email || null,
      address: client.address || null,
      siren: client.siren || null,
      vat_number: client.vat || null,
      purchase_order_ref: client.po_ref || null,
    },

    quote: {
      internal_number: quote.internal_number || null,
      status: quote.status || "draft",
      object: quote.object || null,
      currency: quote.currency || "EUR",
      valid_until: quote.valid_until || null,
      delivery_terms: quote.delivery_terms || null,
      payment_terms: quote.payment_terms || null,
      notes: quote.notes || null,
      exclusions: quote.exclusions || null,
      discount_amount: round2(quote.discount_amount),
      deposit_amount: round2(quote.deposit_amount),
      subtotal_ex_tax: round2(totals.afterDiscount ?? quote.subtotal_ex_tax),
      tax_amount: round2(totals.tax ?? quote.tax_amount),
      total_inc_tax: round2(totals.total ?? quote.total_inc_tax),
    },

    mission: {
      id: mission.id || null,
      title: mission.title || null,
      mission_type: mission.mission_type || null,
      client_id: mission.client_id || null,
      collection_id: mission.collection_id || null,
    },

    invoice_lines: (lines || []).map((l, i) => ({
      sort_order: i,
      kind: l.kind || "service",            // service | expense
      label: l.label || "",
      detail: l.detail || null,
      unit: l.unit || "flat",               // flat | day | hour | artwork | percentage | other
      quantity: num(l.qty),
      unit_price_ex_tax: round2(l.unit_price_ex_tax),
      discount_pct: num(l.discount_pct),
      vat_rate: num(l.vat_rate),
      line_total_ex_tax: lineTotalExTax(l),
    })),

    tax_context: {
      country: (advisorProfile?.country_code) || "FR",
      zone: client.zone || null,
      default_vat_rate: advisorProfile?.default_vat_rate != null ? Number(advisorProfile.default_vat_rate) : null,
      vat_regime: advisorProfile?.vat_regime || null,
      exemption_mention: advisorProfile?.vat_exemption_mention || null,
    },

    attachments: [],
  };
}

/** Télécharge la structure d'export en fichier .json (import dans l'outil de l'Advisor). */
export function downloadQuoteExport(payload, filenameBase) {
  const base = (filenameBase || payload?.quote?.internal_number || "devis").replace(/[^\w.-]+/g, "_");
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${base}.json`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
  return true;
}
